import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSingleProperty, updateProperty, deleteProperty } from "../../api/wordpress";
import PageLoader from "../../components/loaders/page-loader.component";
import BtnLoader from "../../components/loaders/btn-loader.component";
import PropertyRejectionModal from "../../components/modals/property-rejection-modal";
import { nairaFormart, CryptoJSAesJson } from "../../utils/functions";
import { toast } from "sonner";
import AlignedImageBox2 from "../../components/image-boxes/aligned-image-box/aligned-image-box-2";
import { UserContext } from "../../contexts/user.context";

const CryptoJS = require("crypto-js");

const getPropertyOwnerEmail = (property) => {
  if (!property?.author?.hash) return "";

  try {
    return CryptoJS.AES.decrypt(
      property.author.hash,
      /* eslint-disable-next-line */
      process.env.REACT_APP_HASH_KEY,
      { format: CryptoJSAesJson }
    )
      .toString(CryptoJS.enc.Utf8)
      .replaceAll('"', "");
  } catch (error) {
    console.error("Failed to decrypt property owner email:", error);
    return "";
  }
};

function extractContent(s) {
  const span = document.createElement("span");
  span.innerHTML = s;
  return span.textContent || span.innerText;
}

const AdminPropertyReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("user"))?.token;
  const { fsUser } = useContext(UserContext);

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const accessToken = fsUser?.accessToken;

    document.title = "Property Review";
  
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await getSingleProperty(token, { include: [id], status: "pending" });
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        setProperty(data);
      } catch (error) {
        console.error("Error fetching property:", error);
        toast.error("Failed to load property details.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProperty();
  }, [id, token]);

  const sendPropertyApprovalEmail = async (payload) => {
    try {
      await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/emails/send-property-approval-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error("Error sending approval email:", error);
      throw error; // Rethrow to be caught in the calling function
    }
  };

  const sendPropertyRejectionEmail = async (payload) => {
    try {
      await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/emails/send-property-rejection-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error("Error sending rejection email:", error);
      throw error; // Rethrow to be caught in the calling function
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", property.id);
      formData.append("post_status", "publish");

      await updateProperty(token, formData);
      
      const ownerEmail = getPropertyOwnerEmail(property);
      const payload = {
        propertyId: property.id,
        propertyTitle: property.title || "Your Property",
        ownerEmail,
        ownerName: property?.author?.full_name || property?.author?.display_name || "Property Owner",
      };
      try {
        await sendPropertyApprovalEmail(payload);
      } catch (emailError) {
        console.warn("Email sending failed, but property was approved:", emailError);
        // Don't fail the approval if email fails
      }

      toast.success("Property approved and is now live!");
      navigate("/dashboard/admin/approvals");
    } catch (error) {
      console.error(error);
      toast.error("Failed to approve property.");
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setShowRejectionModal(true);
  };

  const handleRejectConfirm = async (rejectionReason) => {
    setActionLoading(true);
    try {
      // Send rejection email first
      const ownerEmail = getPropertyOwnerEmail(property);
      const payload = {
        propertyId: property.id,
        propertyTitle: property.title || "Your Property",
        ownerEmail,
        ownerName: property?.author?.full_name || property?.author?.display_name || "Property Owner",
        rejectionReason: rejectionReason,
      };

      try {
        await sendPropertyRejectionEmail(payload);
      } catch (emailError) {
        console.warn("Email sending failed, continuing with deletion:", emailError);
        // Continue with deletion even if email fails
      }

      // Delete the property
      await deleteProperty(token, property.id);
      setShowRejectionModal(false);
      toast.success("Property has been rejected and owner notified via email.");
      navigate("/dashboard/admin/approvals");
    } catch (error) {
      console.error(error);
      toast.error("Failed to reject property.");
      setActionLoading(false);
      setShowRejectionModal(false);
    }
  };

  if (loading) return <PageLoader size="150px" className="p-10 w-full flex justify-center" />;
  if (!property) return <div className="p-10 text-center text-gray-500">Property not found or already reviewed.</div>;

  const cleanDescription = extractContent(property?.excerpt?.rendered || "").trim();

  const mediaList = [];
  if (property?.acf?.property_video_url) {
    mediaList.push({ type: "video", url: property.acf.property_video_url });
  }

  const receiptUrl = property?.acf?.property_receipt_url || null;
  
  const rawGallery = property?.acf?.gallery || [];
  rawGallery.forEach((item) => {
    const imgUrl = typeof item === "string" ? item : item?.full_image_url || item?.url;
    if (imgUrl) mediaList.push({ type: "image", url: imgUrl });
  });

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 bg-gray-50 min-h-screen">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Review Property</h1>
          <p className="text-sm text-gray-500">Please review the media and details before approving.</p>
        </div>
        <button 
          onClick={() => navigate("/dashboard/admin/approvals")}
          className="text-sm border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-100 transition-colors"
        >
          Back
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        <div className="flex flex-col gap-3">
          <div className="bg-black rounded-xl overflow-hidden shadow-md relative h-64 md:h-96 w-full flex items-center justify-center">
            {mediaList.length > 0 && mediaList[activeMediaIndex]?.type === "video" && (
              <span className="absolute top-4 left-4 bg-yellow-500 text-white text-[10px] uppercase font-bold px-3 py-1 rounded shadow-md z-10 pointer-events-none">
                Mandatory Video Tour
              </span>
            )}

            {mediaList.length > 0 ? (
              mediaList[activeMediaIndex].type === "video" ? (
                <video
                  key={mediaList[activeMediaIndex].url}
                  src={mediaList[activeMediaIndex].url}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  muted
                  playsInline
                />
              ) : (
                <img
                  key={mediaList[activeMediaIndex].url}
                  src={mediaList[activeMediaIndex].url}
                  className="w-full h-full object-cover"
                  alt="Property Preview"
                />
              )
            ) : (
              <div className="text-white text-sm font-medium">No Media Provided</div>
            )}
          </div>

          {mediaList.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {mediaList.map((media, index) => (
                <button
                  key={index}
                  onClick={() => setActiveMediaIndex(index)}
                  className={`relative h-20 w-24 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all duration-200 outline-none ${
                    activeMediaIndex === index
                      ? "border-primary opacity-100 shadow-md"
                      : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  {media.type === "video" ? (
                    <div className="w-full h-full bg-blue-900 flex flex-col items-center justify-center text-white">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mb-1">
                        <div className="w-0 h-0 border-t-4 border-t-transparent border-l-6 border-l-white border-b-4 border-b-transparent ml-1"></div>
                      </div>
                      <span className="text-[10px] font-bold tracking-wider">VIDEO</span>
                    </div>
                  ) : (
                    <img src={media.url} className="w-full h-full object-cover" alt={`Thumbnail ${index}`} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-6 bg-white p-5 md:p-6 rounded-xl shadow-sm border border-gray-200">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" dangerouslySetInnerHTML={{ __html: property.title }} />
            <p className="text-2xl font-bold text-primary mb-6">
              {nairaFormart(property.acf?.price)} <span className="text-sm font-normal text-gray-500">/ {property.acf?.per_duration || "year"}</span>
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 flex flex-col items-center justify-center text-center">
                <span className="text-[10px] uppercase font-bold text-blue-400 mb-1">Type</span>
                <span className="text-sm font-semibold text-primary">{property.property_type || "N/A"}</span>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 flex flex-col items-center justify-center text-center">
                <span className="text-[10px] uppercase font-bold text-blue-400 mb-1">Pref. Gender</span>
                <span className="text-sm font-semibold text-primary capitalize">{property.acf?.preferred_gender || "Any"}</span>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 flex flex-col items-center justify-center text-center col-span-2 md:col-span-1">
                <span className="text-[10px] uppercase font-bold text-blue-400 mb-1">Status</span>
                <span className={`text-sm font-semibold ${property.acf?.occupancy_status ? "text-red-600" : "text-green-600"}`}>
                  {property.acf?.occupancy_status ? "Occupied" : "Vacant"}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Location</h3>
              <p className="text-sm text-gray-800 font-medium">{property.acf?.address || "Address not provided"}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Amenities</h3>
              <div className="flex flex-wrap gap-2">
                {property.acf?.amenities?.length > 0 ? (
                  property.acf.amenities.map((item, i) => (
                    <span key={i} className="bg-white border border-gray-200 text-gray-700 text-xs px-3 py-1.5 rounded-full font-medium shadow-sm">
                      {item.label}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">No amenities listed</span>
                )}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-6">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Description</h3>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{cleanDescription}</p>
            </div>

            {receiptUrl && (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Proof of Payment / Receipt</h3>
                <a
                  href={receiptUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-sm font-semibold text-primary hover:underline"
                >
                  Open receipt attachment
                </a>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Uploader Details</h3>
            <div className="flex items-center p-3 bg-blue-50/50 border border-blue-100 rounded-lg">
                <AlignedImageBox2
                  profileImage={property?.author?.profile_photo?.url}
                  text={property?.author?.full_name || "Unknown User"}
                  subtext={property?.author?.display_name || ""}
                />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mt-auto pt-4">
            <button
              onClick={handleReject}
              disabled={actionLoading}
              className="flex-1 bg-white text-red-600 border-2 border-red-100 hover:bg-red-50 font-bold py-3 md:py-4 rounded-xl disabled:opacity-50 transition-all"
            >
              {actionLoading ? <BtnLoader color={false} /> : "Reject Property"}
            </button>
            <button
              onClick={handleApprove}
              disabled={actionLoading}
              className="flex-1 bg-primary hover:bg-blue-800 text-white font-bold py-3 md:py-4 rounded-xl disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
            >
              {actionLoading ? <BtnLoader color={true} /> : "Approve Listing"}
            </button>
          </div>
        </div>
      </div>

      <PropertyRejectionModal
        isOpen={showRejectionModal}
        onConfirm={handleRejectConfirm}
        onCancel={() => setShowRejectionModal(false)}
        isLoading={actionLoading}
      />
    </div>
  );
};

export default AdminPropertyReview;